import { ApolloQueryResult } from '@apollo/client';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IDuplicateStreamOutputWithPagination,
  RawDuplicateStreamOutputEntity
} from 'src/models/stream';
import { TVisibility } from 'src/models/general';

interface IStream {
  streamPanel: ApolloQueryResult<IDuplicateStreamOutputWithPagination>;
  ignoredStreamPanel: ApolloQueryResult<IDuplicateStreamOutputWithPagination>;
  streamSelected: Array<RawDuplicateStreamOutputEntity>;
  isSelectedAllPrimary: boolean;
  translatedStreams: Array<RawDuplicateStreamOutputEntity>;
}

const initialState: IStream = {
  streamPanel: {
    data: {
      result: [],
      limit: 20,
      page: 1,
      totalRows: 0,
      totalPages: 0
    },
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  ignoredStreamPanel: {
    data: {
      result: [],
      limit: 20,
      page: 1,
      totalRows: 0,
      totalPages: 0
    },
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  streamSelected: [],
  isSelectedAllPrimary: false,
  translatedStreams: []
};

const getStreamSelectionKey = (stream: RawDuplicateStreamOutputEntity) => {
  const objectIds = stream.objectEntityList
    .map((objectEntity) => objectEntity.objectModel.id)
    .sort();

  return `${stream.originalId}:${objectIds.join('|')}`;
};

const matchesExactStream = (
  element: RawDuplicateStreamOutputEntity,
  stream: RawDuplicateStreamOutputEntity
): boolean => getStreamSelectionKey(element) === getStreamSelectionKey(stream);

const matchesStreamObject = (
  element: RawDuplicateStreamOutputEntity,
  stream: RawDuplicateStreamOutputEntity
): boolean => {
  if (element.originalId !== stream.originalId) {
    return false;
  }

  // Check if they share at least one objectModel.id in their objectEntityLists
  return element.objectEntityList.some((elemObj) =>
    stream.objectEntityList.some(
      (streamObj) => elemObj.objectModel.id === streamObj.objectModel.id
    )
  );
};

const updateObjectStreamPanelData = (
  prevTable: Array<RawDuplicateStreamOutputEntity>,
  payload: Array<RawDuplicateStreamOutputEntity>,
  updateProperties: (
    stream: RawDuplicateStreamOutputEntity
  ) => Partial<RawDuplicateStreamOutputEntity>
) => {
  return prevTable.map((currentStream) => {
    const updatedStream = payload.find((stream) =>
      matchesStreamObject(currentStream, stream)
    );

    if (!updatedStream) {
      return currentStream;
    }

    return {
      ...currentStream,
      ...updateProperties(updatedStream)
    };
  });
};

// for updating parent stream
const updateStreamData = (
  prevTable: Array<RawDuplicateStreamOutputEntity>,
  payload: Array<RawDuplicateStreamOutputEntity>,
  updateProperties: (
    stream: RawDuplicateStreamOutputEntity
  ) => Partial<RawDuplicateStreamOutputEntity>
) => {
  return prevTable.map((currentStream) => {
    const updatedStream = payload.find(
      (stream) => stream.originalId === currentStream.originalId
    );

    if (!updatedStream) {
      return currentStream;
    }

    return {
      ...currentStream,
      ...updateProperties(updatedStream)
    };
  });
};

const updateObjectEntityListSentiment = (
  prevTable: Array<RawDuplicateStreamOutputEntity>,
  payload: Array<RawDuplicateStreamOutputEntity>
) => {
  return prevTable.map((currentStream) => {
    const updatedStreams = payload.filter(
      (stream) => stream.originalId === currentStream.originalId
    );

    if (!updatedStreams.length) {
      return currentStream;
    }

    return {
      ...currentStream,
      objectEntityList: currentStream.objectEntityList.map(
        (currentObjectEntity) => {
          const updatedObjectEntity = updatedStreams
            .flatMap((stream) => stream.objectEntityList)
            .find(
              (objectEntity) =>
                objectEntity.objectModel.id ===
                currentObjectEntity.objectModel.id
            );

          if (!updatedObjectEntity) {
            return currentObjectEntity;
          }

          return {
            ...currentObjectEntity,
            finalSentiment: updatedObjectEntity.finalSentiment,
            isEdited: updatedObjectEntity.isEdited
          };
        }
      )
    };
  });
};

const removeObjectEntityListFromStreamPanel = (
  prevTable: Array<RawDuplicateStreamOutputEntity>,
  payload: Array<RawDuplicateStreamOutputEntity>,
  visibility: TVisibility
) => {
  return prevTable.reduce<Array<RawDuplicateStreamOutputEntity>>(
    (updatedTable, currentStream) => {
      const updatedStreams = payload.filter(
        (stream) => stream.originalId === currentStream.originalId
      );

      if (!updatedStreams.length) {
        return [...updatedTable, currentStream];
      }

      const updatedObjectModelIds = updatedStreams.flatMap((stream) =>
        stream.objectEntityList
          .filter(
            (objectEntity) => objectEntity.streamVisibility === visibility
          )
          .map((objectEntity) => objectEntity.objectModel.id)
      );

      const objectEntityList = currentStream.objectEntityList.filter(
        (objectEntity) =>
          !updatedObjectModelIds.includes(objectEntity.objectModel.id)
      );

      if (objectEntityList.length === 0) {
        return updatedTable;
      }

      return [
        ...updatedTable,
        {
          ...currentStream,
          objectEntityList
        }
      ];
    },
    []
  );
};

export const sosmedStreamDuplicate = createSlice({
  name: 'sosmedStreamPanelDuplicate',
  initialState,
  reducers: {
    reducerUpdateStreamPanel: (
      state: IStream,
      action: PayloadAction<
        ApolloQueryResult<IDuplicateStreamOutputWithPagination>
      >
    ) => {
      state.streamPanel = action.payload;
    },
    reducerUpdateStreamPanelByIgnored: (
      state: IStream,
      action: PayloadAction<IDuplicateStreamOutputWithPagination>
    ) => {
      state.streamPanel.data = {
        ...state.streamPanel.data,
        totalRows:
          state.streamPanel.data.totalRows - action.payload.result.length,
        result: state.streamPanel.data.result.filter(
          (currData) =>
            !action.payload.result.find((ignored) =>
              matchesExactStream(ignored, currData)
            )
        )
      };
    },
    reducerUpdateStreamPanelByChange: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      state.streamPanel.data.result = action.payload;
    },
    reducerUpdatePartOfStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      const resultWithUpdatedSentiment = updateObjectEntityListSentiment(
        state.streamPanel.data.result,
        action.payload
      );

      state.streamPanel.data.result = updateObjectStreamPanelData(
        resultWithUpdatedSentiment,
        action.payload,
        (stream) => ({
          label: stream.label
        })
      );
    },
    reducerUpdateIgnoredStreamPanel: (
      state: IStream,
      action: PayloadAction<
        ApolloQueryResult<IDuplicateStreamOutputWithPagination>
      >
    ) => {
      state.ignoredStreamPanel = action.payload;
    },
    reducerUpdateIgnoreStreamPanelByIgnored: (
      state: IStream,
      action: PayloadAction<IDuplicateStreamOutputWithPagination>
    ) => {
      state.ignoredStreamPanel.data = {
        ...state.ignoredStreamPanel.data,
        totalRows:
          state.ignoredStreamPanel.data.totalRows +
          action.payload.result.length,
        result: [
          ...state.ignoredStreamPanel.data.result,
          ...action.payload.result
        ]
      };
    },
    reducerUpdateAllMetricLoading: (
      state: IStream,
      action: PayloadAction<boolean>
    ) => {
      state.streamPanel.loading = action.payload;
      state.ignoredStreamPanel.loading = action.payload;
    },
    // for updating sentiment
    reducerUpdateDataStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      const resultWithUpdatedSentiment = updateObjectEntityListSentiment(
        state.streamPanel.data.result,
        action.payload
      );

      state.streamPanel.data.result = updateObjectStreamPanelData(
        resultWithUpdatedSentiment,
        action.payload,
        (stream) => ({
          label: stream.label
        })
      );
    },
    reducerUpdateObjectEntitySentimentStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      state.streamPanel.data.result = updateObjectEntityListSentiment(
        state.streamPanel.data.result,
        action.payload
      );
    },
    reducerRemoveObjectEntityStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      const nextResult = removeObjectEntityListFromStreamPanel(
        state.streamPanel.data.result,
        action.payload,
        'hidden'
      );

      state.streamPanel.data = {
        ...state.streamPanel.data,
        totalRows:
          state.streamPanel.data.totalRows -
          (state.streamPanel.data.result.length - nextResult.length),
        result: nextResult
      };
    },
    reducerRemoveObjectEntityIgnoredStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      const nextResult = removeObjectEntityListFromStreamPanel(
        state.ignoredStreamPanel.data.result,
        action.payload,
        'show'
      );

      state.ignoredStreamPanel.data = {
        ...state.ignoredStreamPanel.data,
        totalRows:
          state.ignoredStreamPanel.data.totalRows -
          (state.ignoredStreamPanel.data.result.length - nextResult.length),
        result: nextResult
      };
    },
    reducerUpdateEmotionStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      state.streamPanel.data.result = updateStreamData(
        state.streamPanel.data.result,
        action.payload,
        (stream) => ({
          emotion: stream.emotion,
          isEditedEmotion: true
        })
      );
    },
    reducerUpdateLabelStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      state.streamPanel.data.result = updateStreamData(
        state.streamPanel.data.result,
        action.payload,
        (stream) => ({
          label: stream.label
        })
      );
    },
    reducerUpdateStreamSosmedSelected: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      state.streamSelected = action.payload;
    },
    reducerFinishStreamSosmedSelected: (
      state: IStream,
      action: PayloadAction<Array<RawDuplicateStreamOutputEntity>>
    ) => {
      const update = state.streamSelected.filter(
        (streamPrev) =>
          !action.payload.find((streamUpdate) =>
            matchesExactStream(streamUpdate, streamPrev)
          )
      );
      state.streamSelected = update;
    },
    reducerUpdateIsSelectedAllPrimary: (
      state: IStream,
      action: PayloadAction<boolean>
    ) => {
      state.isSelectedAllPrimary = action.payload;
    },
    reducerUpdateTranslatedStreams(
      state: IStream,
      action: PayloadAction<RawDuplicateStreamOutputEntity>
    ) {
      const isStreamExist = state.translatedStreams.find((item) =>
        matchesExactStream(item, action.payload)
      );
      state.translatedStreams = isStreamExist
        ? state.translatedStreams.map((value) =>
            matchesExactStream(value, action.payload)
              ? {
                  ...value,
                  title: action.payload.title,
                  content: action.payload.content
                }
              : value
          )
        : [...state.translatedStreams, action.payload];
    },
    reducerRemoveTranslatedStreams(
      state: IStream,
      action: PayloadAction<RawDuplicateStreamOutputEntity>
    ) {
      state.translatedStreams = state.translatedStreams.filter(
        (item) => !matchesExactStream(item, action.payload)
      );
    }
  }
});

export const {
  reducerUpdateStreamPanel,
  reducerUpdateStreamPanelByIgnored,
  reducerUpdateIgnoreStreamPanelByIgnored,
  reducerUpdateAllMetricLoading,
  reducerUpdateStreamSosmedSelected,
  reducerUpdateDataStreamPanel,
  reducerUpdateIsSelectedAllPrimary,
  reducerUpdateStreamPanelByChange,
  reducerUpdateIgnoredStreamPanel,
  reducerUpdatePartOfStreamPanel,
  reducerFinishStreamSosmedSelected,
  reducerUpdateLabelStreamPanel,
  reducerUpdateObjectEntitySentimentStreamPanel,
  reducerRemoveObjectEntityStreamPanel,
  reducerRemoveObjectEntityIgnoredStreamPanel,
  reducerUpdateEmotionStreamPanel,
  reducerUpdateTranslatedStreams,
  reducerRemoveTranslatedStreams
} = sosmedStreamDuplicate.actions;

export default sosmedStreamDuplicate.reducer;
